/**
 * BulkWhatsAppDialog — pick an approved Interakt template + per-template
 * variable mapping, send to the selected candidates. If the tenant has
 * not configured Interakt yet, surface a direct link to the settings
 * page instead of showing a half-filled form.
 *
 * Variable tokens: either a literal (e.g. "Acme") or a candidate field
 * placeholder like {name} / {first_name} / {job_title} / {company_name}.
 * These render per-recipient on the server.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Phone, Settings, ExternalLink, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTED_TOKENS = ["{name}", "{first_name}", "{job_title}", "{company_name}"];

export default function BulkWhatsAppDialog({ open, count, candidateIds, jobId, onCancel, onDone }) {
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [settings, setSettings] = useState(null); // {configured, key_last4}
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState(""); // "name::lang"
    const [vars, setVars] = useState([]);
    const [result, setResult] = useState(null);
    const [preview, setPreview] = useState(null); // { rendered_body, candidate, values }
    const [previewBusy, setPreviewBusy] = useState(false);

    useEffect(() => {
        if (!open) {
            setResult(null); setSelected(""); setVars([]); setPreview(null);
            return;
        }
        (async () => {
            setLoading(true);
            try {
                const [s, t] = await Promise.all([
                    api.get("/company/interakt/settings"),
                    api.get("/company/interakt/templates"),
                ]);
                setSettings(s.data || null);
                setTemplates(t.data?.templates || []);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally { setLoading(false); }
        })();
    }, [open]);

    const activeTpl = templates.find((t) => `${t.name}::${t.language_code}` === selected) || null;

    // Reset variable row when template changes
    useEffect(() => {
        if (!activeTpl) { setVars([]); return; }
        const n = Number(activeTpl.body_variables_count || 0);
        setVars((cur) => {
            const next = Array(n).fill("");
            for (let i = 0; i < Math.min(cur.length, n); i++) next[i] = cur[i];
            return next;
        });
    }, [selected, activeTpl]);

    // Clear preview when template or variable mapping changes — stale previews
    // mislead recruiters who could ship typos they thought they fixed.
    useEffect(() => { setPreview(null); }, [selected, vars]);

    const canSend = !!activeTpl && !busy && !!settings?.configured && (candidateIds?.length || 0) > 0;

    const runPreview = async () => {
        if (!activeTpl || !(candidateIds?.length)) return;
        setPreviewBusy(true);
        try {
            const { data } = await api.post("/company/interakt/preview", {
                template_name: activeTpl.name,
                language_code: activeTpl.language_code,
                candidate_id: candidateIds[0],
                variable_map: vars,
                job_id: jobId || null,
            });
            setPreview(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setPreviewBusy(false); }
    };

    const submit = async () => {
        if (!activeTpl) return;
        setBusy(true);
        try {
            const { data } = await api.post("/company/interakt/bulk-send", {
                template_name: activeTpl.name,
                language_code: activeTpl.language_code,
                candidate_ids: candidateIds,
                variable_map: vars,
                job_id: jobId || null,
            });
            setResult(data);
            const okMsg = `WhatsApp — ${data?.accepted || 0} accepted · ${data?.no_phone || 0} no-phone · ${data?.failed || 0} failed`;
            if ((data?.failed || 0) > 0 || (data?.no_phone || 0) > 0) toast.warning(okMsg);
            else toast.success(okMsg);
            // Parent is notified only when the user explicitly closes the
            // dialog (via the Close button). Firing onDone here would unmount
            // the dialog immediately and the Delivery summary panel would
            // never render — a dead-UI we deliberately avoid.
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
            <DialogContent className="max-w-xl" data-testid="jobtable-bulk-whatsapp-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Phone className="w-5 h-5 text-emerald-600" />
                        Send WhatsApp to {count} candidate{count === 1 ? "" : "s"}
                    </DialogTitle>
                    <DialogDescription>
                        Pick an approved template and map the variables.
                        Each candidate gets their own message, personalized by their row data.
                        Candidates without a phone number are skipped (logged as <code className="text-xs">no_phone</code>).
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading templates…
                    </div>
                ) : !settings?.configured ? (
                    <div className="border border-amber-300 bg-amber-50 p-4 text-sm">
                        <p className="font-semibold text-amber-900 mb-1">Interakt isn't configured yet</p>
                        <p className="text-amber-800 text-xs mb-3">
                            Add your Interakt API key and sync approved templates before sending WhatsApp from here.
                        </p>
                        <Link to="/company/whatsapp" onClick={onCancel}
                            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest border border-amber-700 text-amber-900 px-2 py-1.5 hover:bg-amber-100"
                            data-testid="jobtable-bulk-whatsapp-open-settings">
                            <Settings className="w-3 h-3" /> Open WhatsApp settings
                            <ExternalLink className="w-3 h-3" />
                        </Link>
                    </div>
                ) : templates.length === 0 ? (
                    <div className="border border-border bg-secondary/40 p-4 text-sm">
                        <p className="font-semibold mb-1">No approved templates yet</p>
                        <p className="text-xs text-muted-foreground mb-3">
                            Register at least one template under WhatsApp settings. Copy the template <strong>code name</strong>
                            and body from your Interakt dashboard.
                        </p>
                        <Link to="/company/whatsapp" onClick={onCancel}
                            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest border border-border px-2 py-1.5 hover:bg-secondary">
                            <Settings className="w-3 h-3" /> Add templates
                            <ExternalLink className="w-3 h-3" />
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block mb-1">
                                Approved template
                            </label>
                            <select
                                value={selected}
                                onChange={(e) => setSelected(e.target.value)}
                                className="w-full border border-border bg-white px-3 py-2 text-sm rounded-none focus:outline-none focus:border-ikb"
                                data-testid="jobtable-bulk-whatsapp-template"
                            >
                                <option value="">Select a template…</option>
                                {templates.map((t) => (
                                    <option key={`${t.name}::${t.language_code}`} value={`${t.name}::${t.language_code}`}>
                                        {t.name} · {t.language_code} · {t.body_variables_count || 0} var{t.body_variables_count === 1 ? "" : "s"}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {activeTpl && (
                            <div className="border border-border bg-secondary/40 p-3 text-xs whitespace-pre-wrap font-mono"
                                 data-testid="jobtable-bulk-whatsapp-body">
                                {activeTpl.body}
                            </div>
                        )}

                        {activeTpl && Number(activeTpl.body_variables_count || 0) > 0 && (
                            <div className="space-y-2">
                                <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block">
                                    Variables ({activeTpl.body_variables_count})
                                </label>
                                <p className="text-[11px] text-muted-foreground">
                                    Use a candidate field like <code>{"{name}"}</code>, <code>{"{first_name}"}</code>, <code>{"{job_title}"}</code>, <code>{"{company_name}"}</code> — or type a literal.
                                </p>
                                {vars.map((v, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono text-muted-foreground w-10 shrink-0">{`{{${i + 1}}}`}</span>
                                        <Input
                                            value={v}
                                            onChange={(e) => setVars((cur) => cur.map((x, j) => j === i ? e.target.value : x))}
                                            placeholder={SUGGESTED_TOKENS[i] || "literal or {token}"}
                                            disabled={busy}
                                            data-testid={`jobtable-bulk-whatsapp-var-${i}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTpl && (
                            <div className="flex items-center gap-2">
                                <Button type="button" variant="outline"
                                    onClick={runPreview}
                                    disabled={previewBusy || busy || !(candidateIds?.length)}
                                    className="rounded-none text-xs h-8"
                                    data-testid="jobtable-bulk-whatsapp-preview-btn">
                                    {previewBusy
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                                        : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                                    Preview against first candidate
                                </Button>
                            </div>
                        )}

                        {preview && (
                            <div className="border border-ikb/30 bg-ikb/5 p-3 text-xs space-y-1.5"
                                 data-testid="jobtable-bulk-whatsapp-preview-panel">
                                <div className="font-mono uppercase tracking-widest text-[10px] text-ikb">
                                    Rendered for {preview.candidate?.name || "candidate"}
                                    {preview.candidate?.phone_cc && (
                                        <span className="ml-2 text-foreground/70">
                                            → {preview.candidate.phone_cc} {preview.candidate.phone_national}
                                        </span>
                                    )}
                                    {!preview.candidate?.has_phone && (
                                        <span className="ml-2 text-rose-700">· no usable phone</span>
                                    )}
                                </div>
                                <div className="whitespace-pre-wrap font-mono bg-white border border-border p-2"
                                     data-testid="jobtable-bulk-whatsapp-preview-body">
                                    {preview.rendered_body}
                                </div>
                                {(preview.values || []).some((v) => !v) && (
                                    <p className="text-[11px] text-amber-800">
                                        Some variables resolved to empty strings — double-check your mapping before sending.
                                    </p>
                                )}
                            </div>
                        )}

                        {result && (
                            <div className="border border-border bg-white p-3 text-xs">
                                <p className="font-mono uppercase tracking-widest text-muted-foreground mb-1">Delivery summary</p>
                                <p>
                                    <span className="text-emerald-700 font-semibold">{result.accepted || 0}</span> accepted ·
                                    <span className="text-amber-700 font-semibold"> {result.no_phone || 0}</span> no phone ·
                                    <span className="text-rose-700 font-semibold"> {result.failed || 0}</span> failed
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={result ? onDone : onCancel} disabled={busy} className="rounded-none"
                        data-testid="jobtable-bulk-whatsapp-cancel">
                        {result ? "Close" : "Cancel"}
                    </Button>
                    {!result && (
                        <Button onClick={submit} disabled={!canSend}
                            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="jobtable-bulk-whatsapp-send">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Phone className="w-4 h-4 mr-1.5" />}
                            Send to {count}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
